import styled, { css } from 'styled-components';
import CommonTypography from '@src/new-components/Typography';
import { COLOR } from '@src/styles/color';

// Chữ trong ô số dùng chung cho slider và ô nhập, tách ra khỏi viền hộp.
const numberText = css`
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.4px;

  /* Bỏ mũi tên tăng giảm mặc định của input number. */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const StyledConfigRow = styled.div`
  /* Hàng chưa dùng được thì làm mờ chứ không ẩn, để người dùng biết là có. */
  &.config-row-disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;

  .config-row-label {
    flex-shrink: 0;
    white-space: nowrap;
    color: ${COLOR.colorGrayV2};
  }

  .config-row-control {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }
`;

// Bề rộng do hàng truyền vào, các chip chia đều phần còn lại.
export const StyledChipTabs = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  width: ${({ $width }) => $width}px;
  padding: 4px;
  border: 0.5px solid ${COLOR.indigo[16]};
  border-radius: 8px;
  background-color: ${COLOR.charcoalV2[64]};

  .chip-tab {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1 0 0;
    /* Thiếu dòng này flex không cho chip nhỏ hơn icon cộng padding là 36px,
       nên chip 32px của mục Vị trí bị phình ra. */
    min-width: 0;
    height: 32px;
    padding: 8px;
    border: none;
    border-radius: 8px;
    background: none;
    cursor: pointer;

    svg {
      width: 20px;
      height: 20px;
      color: ${COLOR.indigo[80]};
    }
  }

  .chip-tab:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  .chip-tab-active {
    background-color: ${COLOR.white};
    filter: drop-shadow(0px 2px 16px rgba(0, 0, 0, 0.08));
  }
`;

export const StyledNumberInput = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  width: ${({ $width }) => $width}px;
  height: 40px;
  padding: 8px;
  border: 0.5px solid ${COLOR.indigo[16]};
  border-radius: 8px;
  background-color: ${COLOR.white};

  input {
    ${numberText}
    flex: 1 0 0;
    min-width: 0;
    border: none;
    outline: none;
    background: none;
    color: ${COLOR.colorGrayV2};

    /* Đang sửa thì chữ đậm lên, rời ô lại nhạt như cũ. */
    &:focus {
      color: ${COLOR.descriptionGray};
    }
  }

  .number-input-suffix {
    flex-shrink: 0;
    color: ${COLOR.colorGrayV2};
  }
`;

// Font dropdown trigger: the same 40px shape as SelectField, hand-built so it
// can carry the cascading family -> weight menu a one-level Select cannot.
// Hai dropdown của panel dùng chung một dáng — trigger, menu và hàng menu để
// cùng một chỗ cho khỏi lệch nhau.
export const MENU_PAPER_SX = {
  mt: 0.5,
  maxHeight: 320,
  overflowY: 'auto',
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.16)',
};

export const MENU_ITEM_SX = {
  fontSize: '14px',
  minHeight: 'auto',
  py: '6px',
  px: '12px',
  // MUI tô hàng đang chọn bằng màu thương hiệu; đổi về xám.
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: COLOR.black[16],
  },
};

export const StyledSelectTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  width: ${({ $width }) => $width}px;
  height: 40px;
  padding: 10px 8px 10px 16px;
  border: 0.5px solid ${COLOR.indigo[16]};
  border-radius: 8px;
  background-color: ${COLOR.white};
  cursor: pointer;

  .label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: -0.4px;
    color: ${COLOR.descriptionGray};
  }

  .weight {
    flex-shrink: 0;
    font-size: 12px;
    line-height: 20px;
    color: ${COLOR.light};
  }

  .arrow {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    color: ${COLOR.indigo[80]};
  }
`;

export const StyledSliderField = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  width: ${({ $width }) => $width}px;
  height: 40px;
  padding: 0 4px 0 12px;
  border: 0.5px solid ${COLOR.indigo[16]};
  border-radius: 8px;
  background-color: ${COLOR.white};

  .slider-field-track {
    flex: 1 0 0;
    min-width: 0;
    /* Panel có reset border-box nên chiều cao gốc của MUI tính sai. Để thanh
       tự đặt chiều cao, còn padding chỉ để nới vùng bấm cho dễ kéo. */
    height: auto;
    padding: 12px 0;
    color: ${COLOR.indigo[100]};

    .MuiSlider-rail,
    .MuiSlider-track {
      height: 4px;
      border-radius: 2px;
      /* MUI viền phần đã chạy bằng chính màu của nó, thành ra dày hơn 4px. */
      border: none;
    }

    .MuiSlider-rail {
      opacity: 1;
      background-color: ${COLOR.indigo[8]};
    }

    /* Thiết kế chỉ có thanh, không có núm; giữ núm nhưng ẩn cho tới khi dùng tới. */
    .MuiSlider-thumb {
      width: 12px;
      height: 12px;
      opacity: 0;

      &:hover,
      &.Mui-focusVisible,
      &.Mui-active {
        opacity: 1;
        box-shadow: none;
      }
    }
  }

  /* Thiết kế ghi 56px, vừa đúng ba chữ số — "1000%" của độ giãn ngang nền bị
     cắt mất chữ số đầu. Nới đều cho mọi thanh, nới riêng một cái thì thanh
     trượt hàng đó ngắn hơn và lệch hàng. */
  .slider-field-value {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    width: 60px;
    padding: 0 4px;
    color: ${COLOR.colorGrayV2};

    input {
      ${numberText}
      flex: 1 0 0;
      min-width: 0;
      border: none;
      outline: none;
      background: none;
      color: inherit;
      text-align: center;

      /* Đang sửa thì chữ đậm lên, rời ô lại nhạt như cũ. */
      &:focus {
        color: ${COLOR.descriptionGray};
      }
    }

    /* Có đơn vị thì dồn số sát vào nó, đọc liền thành "80%". */
    input:not(:only-child) {
      text-align: right;
    }
  }
`;

export const StyledColorField = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;

  .color-field-swatch {
    position: relative;
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border: 0.5px solid ${COLOR.indigo[16]};
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;

    /* Ô chọn màu của trình duyệt không style được, phủ trong suốt lên ô màu. */
    input {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      padding: 0;
      border: none;
      opacity: 0;
      cursor: pointer;
    }
  }

  .color-field-hex {
    ${numberText}
    width: ${({ $hexWidth }) => $hexWidth}px;
    height: 40px;
    /* Chữ đã căn giữa nên bỏ padding ngang, ô hẹp 80px mới đủ chỗ cho #RRGGBB. */
    padding: 0;
    border: 0.5px solid ${COLOR.indigo[16]};
    border-radius: 8px;
    outline: none;
    background-color: ${COLOR.white};
    color: ${COLOR.descriptionGray};
    text-align: center;
    text-transform: uppercase;
  }
`;

export const StyledNumberField = styled.input`
  ${numberText}
  flex-shrink: 0;
  width: ${({ $width }) => $width}px;
  height: 40px;
  padding: 0 16px;
  border: 0.5px solid ${COLOR.indigo[16]};
  border-radius: 8px;
  outline: none;
  background-color: ${COLOR.white};
  color: ${COLOR.descriptionGray};
`;

export const StyledRadioField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .radio-option,
  .radio-option-label {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .radio-option-label {
    cursor: pointer;
  }

  .MuiRadio-root {
    padding: 2px;
    color: ${COLOR.indigo[64]};

    &.Mui-checked {
      color: ${COLOR.indigo[100]};
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .radio-label {
    color: ${COLOR.descriptionGray};
  }
`;

export const StyledBadge = styled(CommonTypography)`
  flex-shrink: 0;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: ${COLOR.bluev2[8]};
  color: ${COLOR.blueV2};
`;

export const StyledGroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  .group-header-title {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;
